import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { getBooleanAttributeValue } from "../utils";
import { numberWithCommas } from 'shared-ui/src/utils/commons'

export const ID = "#star-rating";

const STAR_SIZES = {
    mini: "mini-star",
    small: "small-star",
    medium: "medium-star",
    large: "large-star",
    xlarge: "xlarge-star"
};

const STAR_CONTAINER_SIZES = {
    mini: "mini-stars-container",
    small: "small-stars-container",
    medium: "medium-stars-container",
    large: "large-stars-container",
};

const STAR_DIVIDER = 4.87;
const MIN_RATING_FOR_LDP_STORES = 4;

export class StarRatingComponent extends React.Component {
    constructor(props) {
        super(props);

        this.ratingValue = Number(this.props.rating > 5 ? 5 : this.props.rating.toFixed(1));
        this.ratingValue = this.hasLDP && this.ratingValue < MIN_RATING_FOR_LDP_STORES ? 0 : this.ratingValue;

        this.additionalStylesMap = {
            starContainerSize: STAR_CONTAINER_SIZES.hasOwnProperty(
                this.props.size
            )
                ? STAR_CONTAINER_SIZES[this.props.size]
                : STAR_CONTAINER_SIZES["small"],

            wrapText: this.props.wrapRatingText && "wrap-rating-text",
            wrapTextMobile: this.props.wrapRatingTextOnMobile && "wrap-rating-text-mobile",
            alignItems: `align-items-${this.props.alignItems}`,
            oneLine: this.props.oneLine && "flex-row",

            justifyContent:
                !this.props.showRating &&
                `justify-content-${this.props.justifyContent}`,

            verticalText: this.props.verticalText && "vertical-text",
        };

        this.additionalClassnames = Object.keys(this.additionalStylesMap)
            .filter((property) => this.additionalStylesMap[property])
            .map((truthyProperty) => this.additionalStylesMap[truthyProperty])
            .join(" ");
    }

    renderEmptyStars() {
        let items = [];

        for (let i = 0; i < 5; i++) {
            items.push(
                <i
                    key={`star-empty-${i}`}
                    className={`fa fa-star-o ${
                        STAR_SIZES.hasOwnProperty(this.props.size)
                            ? STAR_SIZES[this.props.size]
                            : STAR_SIZES["small"]
                    }`}
                />
            );
        }

        return items;
    }

    renderFilledStars() {
        let items = [];

        for (let i = 0; i < 5; i++) {
            items.push(
                <i
                    key={`star-filled-${i}`}
                    className={`fa fa-star ${
                        STAR_SIZES.hasOwnProperty(this.props.size)
                            ? STAR_SIZES[this.props.size]
                            : STAR_SIZES["small"]
                    }`}
                />
            );
        }

        return items;
    }

    render() {

        const tooltipTitle = this.ratingValue > 0
                                ? `${this.ratingValue.toFixed(1)} out of 5.0 stars`
                                : 'Not rated yet';

        return (
            <div
                className={`stars ${this.additionalClassnames}`}
                style={{ width: "100%" }}
            >
                <div
                    className="empty-stars"
                    title={tooltipTitle}
                    data-toggle="tooltip"
                    data-placement={this.props.tooltipPlacement || 'bottom'}
                >

                    {this.renderEmptyStars()}

                    <div
                        className="filled-stars"
                        style={{
                            width: `${(this.ratingValue / STAR_DIVIDER) * 100}%`,
                        }}
                    >
                        {this.renderFilledStars()}
                    </div>
                </div>

                {this.props.showRating && (
                    <div className={`star-rating-labels-wrapper ${this.props.inlineText && 'text-nowrap'}`}>
                        <div
                            className={`star-rating-label ${this.props.displayInlineText && 'd-inline'}`}
                        >
                            {this.ratingValue === 0 || // ldp-retailers with bad rating appear as not having rating at all
                            this.props.ratingCount === 0 ? (
                                <>
                                    {!this.props.withoutNotRatedText && (
                                        <span>Not rated yet. </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    {this.props.ratingUrl ? (
                                        <a href={this.props.ratingUrl}>
                                            {this.props.withNoLabelText
                                                ? ""
                                                : "Based on"}{" "}
                                            {numberWithCommas(this.props.ratingCount)}{" "}
                                            {this.props.ratingCount > 1
                                                ? "ratings "
                                                : "rating "}
                                        </a>
                                    ) : (
                                        <span>
                                            {this.props.withNoLabelText
                                                ? ""
                                                : "Based on"}{" "}
                                            {numberWithCommas(this.props.ratingCount)}{" "}
                                            {this.props.ratingCount > 1
                                                ? "ratings "
                                                : "rating "}
                                        </span>
                                    )}
                                </>
                            )}

                            {this.props.showReview && (
                                <div
                                    className={`star-rating-label ${this.props.displayInlineText && 'd-inline'}`}
                                >
                                    {(this.ratingValue === 0 || (this.props.reviewCount === 0 && this.props.ratingCount === 0)) && (
                                        <a
                                            className="first-rate"
                                            href={this.props.reviewUrl} // URL to store review form
                                        >
                                            Write a review
                                        </a>
                                    )}
                                    {this.ratingValue !== 0 &&
                                        this.props.reviewCount > 0 && (
                                            <>
                                                <span>+</span>
                                                <a href={this.props.reviewUrl}>
                                                    {numberWithCommas(this.props.reviewCount)}{" "}
                                                    {this.props.reviewCount > 1
                                                        ? "reviews"
                                                        : "review"}
                                                </a>
                                            </>
                                        )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

StarRatingComponent.propTypes = {
    size: PropTypes.oneOf(["mini", "small", "medium", "large", "xlarge"]),
    rating: PropTypes.number,
    ratingCount: PropTypes.number,
    reviewCount: PropTypes.number,
    reviewUrl: PropTypes.string,
    ratingUrl: PropTypes.string,
    hasLDP: PropTypes.bool,
    justifyContent: PropTypes.oneOf(["start", "center", "end"]),
    withoutNotRatedText: PropTypes.bool,
    withNoLabelText: PropTypes.bool,
    showRating: PropTypes.bool,
    showReview: PropTypes.bool,
    tooltipPlacement: PropTypes.oneOf(["top", "right", "bottom", "left"]),
    oneLine: PropTypes.bool, // Make all the component to be in just one line
    verticalText: PropTypes.bool, // Make labels to be vertically aligned
    inlineText: PropTypes.bool, // Label's text are in just one row
    displayInlineText: PropTypes.bool, // Makes star rating labels to have display: inline
    alignItems: PropTypes.oneOf(["start", "center", "end"]),
    wrapRatingText: PropTypes.bool,
    wrapRatingTextOnMobile: PropTypes.bool
};

export function renderStarComponent(selector = ID) {
    const starRatingInstances = Array.from(document.querySelectorAll(selector));

    for (let element of starRatingInstances) {
        if (element) {
            const size = element.getAttribute("data-size");
            const rating = element.getAttribute("data-rating");
            const ratingCount = element.getAttribute("data-rating-count");
            const ratingUrl = element.getAttribute("data-rating-url");
            const reviewCount = element.getAttribute("data-review-count");
            const reviewUrl = element.getAttribute("data-review-url");
            const justifyContent = element.getAttribute("data-justify-content");
            const alignItems = element.getAttribute("data-align-items");
            const tooltipPlacement = element.getAttribute("data-tooltip-placement");

            // Get value of boolean attributes
            const wrapRatingText = getBooleanAttributeValue(
                element.getAttribute("data-wrap-rating-text"),
                false
            );

            const hasLDP = getBooleanAttributeValue(
                element.getAttribute("data-ldp"),
                false
            );

            const wrapRatingTextOnMobile = getBooleanAttributeValue(
                element.getAttribute("data-wrap-rating-text-on-mobile"),
                false
            );

            const oneLine = getBooleanAttributeValue(
                element.getAttribute("data-one-line"),
                false
            )
            const verticalText = getBooleanAttributeValue(
                element.getAttribute("data-vertical-text"),
                false
            );
            const inlineText = getBooleanAttributeValue(
                element.getAttribute("data-inline-text"),
                false
            );
            const displayInlineText = getBooleanAttributeValue(
                element.getAttribute("data-display-inline-text"),
                false
            )
            const withoutNotRatedText = getBooleanAttributeValue(
                element.getAttribute("data-without-not-rated-text"),
                false
            );
            const withNoLabelText = getBooleanAttributeValue(
                element.getAttribute("data-with-no-label-text"),
                false
            );
            const showRating = getBooleanAttributeValue(
                element.getAttribute("data-show-rating"),
                true
            );
            const showReview = getBooleanAttributeValue(
                element.getAttribute("data-show-review"),
                false
            );

            ReactDOM.render(
                <StarRatingComponent
                    size={size}
                    rating={
                        rating && rating !== "None" ? parseFloat(rating) : 0
                    }
                    ratingCount={
                        ratingCount && ratingCount !== "None"
                            ? parseInt(ratingCount)
                            : 0
                    }
                    reviewCount={
                        reviewCount && reviewCount !== "None"
                            ? parseInt(reviewCount)
                            : 0
                    }
                    reviewUrl={reviewUrl}
                    ratingUrl={ratingUrl}
                    hasLDP={hasLDP}
                    verticalText={verticalText}
                    justifyContent={justifyContent ? justifyContent : "center"}
                    alignItems={alignItems ? alignItems : "center"}
                    showReview={showReview}
                    showRating={showRating}
                    tooltipPlacement={tooltipPlacement}
                    withoutNotRatedText={withoutNotRatedText}
                    withNoLabelText={withNoLabelText}
                    inlineText={inlineText}
                    displayInlineText={displayInlineText}
                    oneLine={oneLine}
                    wrapRatingText={wrapRatingText}
                    wrapRatingTextOnMobile={wrapRatingTextOnMobile}
                />,
                element
            );
        }
    }
}
